/**
 * The tunables for the two things that go wrong *around* the aeroplane rather
 * than to it — the man in the cockpit (`physiology.ts`: hypoxia and bleeding
 * out) and the weather in the carburettor (`carburetor.ts`) — split out of
 * `tunables.ts` the same way `gear-tunables.ts` and `gunnery-tunables.ts` were.
 * They merge straight back into `DEFAULT_TUNABLES`, so nothing downstream sees
 * more than one table.
 *
 * The manual is the only source either block has, and it gives mechanics
 * without numbers: pilots "who flew above 20,000 feet for too long would get
 * dizzy and occasionally even blackout", and a carburettor ices up at altitude
 * and thaws again "at a much lower altitude". Only the 20,000 ft threshold is
 * a stated figure; everything else here is CALIBRATION-PENDING and the
 * acceptance tests assert the manual's sentences, not these values.
 */

export const PILOT_TUNABLES = {
  /** Manual: pilots "who flew above 20,000 feet for too long would get dizzy and occasionally even blackout". */
  hypoxiaAltitudeFt: 20000,
  /** Foot-seconds above the threshold needed to fill the hypoxia meter. CALIBRATION-PENDING; 300000 ⇒ about a minute at 25,000 ft. */
  hypoxiaFootSecondsToBlackout: 300000,
  /** Meter drain per second once back below the threshold — the manual's "wake before hitting the ground". CALIBRATION-PENDING. */
  hypoxiaRecoveryPerSecond: 0.05,
  /** Meter level at which dizziness starts. CALIBRATION-PENDING. */
  hypoxiaDizzyThreshold: 0.5,
  /** Meter level at which the pilot blacks out and the controls lock. CALIBRATION-PENDING. */
  hypoxiaBlackoutThreshold: 1,
  /** Meter level the pilot must drain back below to regain control. CALIBRATION-PENDING. */
  hypoxiaWakeThreshold: 0.6,
  /** Seconds from wounding to bleeding out if the pilot never lands. CALIBRATION-PENDING — not in any `.DAT` (flight-tunables.md §8). */
  woundBleedOutSeconds: 300,
  /** Seconds of `bleedOutSeconds` remaining at which the `bleedOutWarning` event fires — the "your wound is getting serious" cue. CALIBRATION-PENDING, same source as `woundBleedOutSeconds`. */
  woundBleedOutWarningSeconds: 60,

  /** Altitude above which the carburettor can ice up. CALIBRATION-PENDING — the manual gives the mechanic and no numbers. */
  carbFreezeAltitudeFt: 14000,
  /** Altitude the aircraft must descend below before a thaw can start — the manual's "much lower altitude" hysteresis. CALIBRATION-PENDING. */
  carbThawAltitudeFt: 8000,
  /** Icing hazard per second above the freeze altitude. CALIBRATION-PENDING. */
  carbFreezeProbabilityPerSecond: 0.004,
  /** Seconds below the thaw altitude before power returns. CALIBRATION-PENDING. */
  carbThawDelaySeconds: 20,
  /** Season/weather multiplier on the icing hazard — the manual's "especially during winter months". CALIBRATION-PENDING. */
  carbSeasonFactor: 1,
} satisfies Record<string, number>;

export type PilotTunableKey = keyof typeof PILOT_TUNABLES;

/** All but `hypoxiaAltitudeFt`, which the manual states outright. */
export const PILOT_TUNABLE_KEYS: readonly PilotTunableKey[] = (
  Object.keys(PILOT_TUNABLES) as PilotTunableKey[]
).filter((key) => key !== "hypoxiaAltitudeFt");
